import { type FC, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBillingEntitlements } from "../billing/useSubscription";
import { Modal, useModal } from "../common/modal/Modal";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { LinkGen } from "../LinkGen";
import {
	useReviewReplyRules,
	useReviewReplyRulesActions,
} from "../settings/useIntegrations";

const STAR_RULE_OPTIONS = [5, 4, 3, 2, 1] as const;
/** Empty = require approval for every star rating (no auto-send). */
const DEFAULT_AUTO_SEND: number[] = [];

export const ModalReviewReplyRules: FC = () => {
	const { close } = useModal();
	const navigate = useNavigate();
	const company = useCompany();
	const { data: entitlements } = useBillingEntitlements();
	const canAutoReply = entitlements.autoReplyRules;
	const rules = useReviewReplyRules();
	const { updateRules } = useReviewReplyRulesActions();
	const [draft, setDraft] = useState<number[]>(
		() => rules.data?.autoSendRatings ?? DEFAULT_AUTO_SEND,
	);
	const [saving, setSaving] = useState(false);

	const setStarRule = (stars: number, autoSend: boolean) => {
		const next = new Set(draft);
		if (autoSend) {
			next.add(stars);
		} else {
			next.delete(stars);
		}
		setDraft(Array.from(next).sort((a, b) => b - a));
	};

	const handleSave = async () => {
		if (!canAutoReply) {
			return;
		}
		setSaving(true);
		try {
			await updateRules.mutateAsync(draft);
			close();
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal>
			<h2 className="pb-2">Review Auto-Reply Rules</h2>
			{canAutoReply ? (
				<p
					style={{
						margin: "0 0 16px",
						color: "var(--bs-secondary-color)",
						fontSize: 13,
					}}
				>
					Configure how Moxie handles reviews for {company.name}. By default
					every reply waits for your approval. Only turn on Auto-send for
					ratings you trust Moxie to post without review.
				</p>
			) : (
				<p
					style={{
						margin: "0 0 16px",
						color: "var(--bs-secondary-color)",
						fontSize: 13,
					}}
				>
					Auto-send rules are included on full-service (Agency) plans. On your
					current plan Moxie still drafts replies — every send needs your
					approval.{" "}
					<Link to={LinkGen.Settings.Billing()} onClick={close}>
						Upgrade in Billing
					</Link>
					.
				</p>
			)}
			<div style={{ display: "grid", gap: 8 }}>
				{STAR_RULE_OPTIONS.map((stars) => {
					const autoSend = canAutoReply && draft.includes(stars);
					return (
						<label
							key={stars}
							style={{
								display: "grid",
								gridTemplateColumns: "88px 1fr",
								alignItems: "center",
								gap: 12,
								margin: 0,
								fontSize: 14,
							}}
						>
							<span style={{ fontWeight: 600 }}>{stars} ★</span>
							<select
								className="form-select form-select-sm"
								aria-label={`${stars} star review rule`}
								disabled={saving || rules.isLoading || !canAutoReply}
								value={autoSend ? "auto" : "approve"}
								onChange={(e) => setStarRule(stars, e.target.value === "auto")}
							>
								<option value="auto">Auto-send</option>
								<option value="approve">Require approval</option>
							</select>
						</label>
					);
				})}
			</div>
			<div className="mt-4 d-flex justify-content-end gap-2" style={{ gap: 8 }}>
				<Button variant="ghost" size="sm" disabled={saving} onClick={close}>
					Close
				</Button>
				{canAutoReply ? (
					<Button
						size="sm"
						loading={saving}
						disabled={saving || rules.isLoading}
						onClick={handleSave}
					>
						Save
					</Button>
				) : (
					<Button
						size="sm"
						onClick={() => {
							close();
							navigate(LinkGen.Settings.Billing());
						}}
					>
						View plans
					</Button>
				)}
			</div>
		</Modal>
	);
};
